import { getCookie, setCookie } from '@core/utils'
import { matchesPromotion } from './utils'
import type { EventName, ItemsResponse } from '@src/types'


// Só estes eventos podem ANOTAR produtos no cookie, porque neles o cliente mostrou intenção real
// (clicou no produto ou adicionou à sacola). view_item_list fica de fora de propositalmente
const RELATED_KEYS_WRITERS: EventName[] = ['SELECT_ITEM', 'ADD_TO_CART', 'CUSTOM_ADD_TO_CART']
const PROMO_COOKIE = '__azzas_trk_promo'
const PROMO_TTL_MS = 10 * 60 * 1000 // janela pra ANOTAR: até 10 min depois do clique no banner, produtos ainda podem ser vinculados à promoção
const ANNOTATED_TTL_MS = 24 * 60 * 60 * 1000 // promoção COM produto anotado sobrevive até 24h (ou até o purchase, que limpa tudo)
const MAX_PROMOTIONS = 5 // máximo de promoções guardadas ao mesmo tempo; entrando a 6ª, a mais antiga é apagada
const MAX_RELATED_KEYS = 30 // máximo de produtos anotados no cookie, pra não estourar o limite de 4KB do browser

export type StoredPromotion = {
  id: string // "apelido" único da promoção; é ele que o related_keys usa pra dizer de qual promoção o produto veio
  promotion_name: string | null
  creative_name: string | null
  creative_slot: string | null
  promotion_url: string | null
  saved_at: number
}

// O que fica salvo no cookie: a lista de promoções ativas + as anotações de qual produto veio de qual promoção
export type PromoCookieData = {
  promos: StoredPromotion[]
  related_keys: Record<string, string> // lê-se: { "item_id do produto": "id da promoção de onde ele veio" }
}

type PromotionInput = Omit<StoredPromotion, 'id' | 'saved_at'>

function promotionId(input: PromotionInput): string {
  const str = [input.promotion_name, input.creative_name, input.creative_slot, input.promotion_url].join('|')
  let hash = 5381
  for (let i = 0; i < str.length; i++) hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  return (hash >>> 0).toString(36)
}

let cachedRootDomain: string | null | undefined
// Descobre o domínio raiz do site (ex: "farmrio.com.br") pra gravar o cookie nele e valer em todos os subdomínios.
function resolveRootDomain(): string | null {
  if (cachedRootDomain !== undefined) return cachedRootDomain
  if (typeof document === 'undefined') return null

  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost'
  const isIpAddress = /^[\d.]+$/.test(hostname)

  if (!hostname || isLocalhost || isIpAddress) {
    cachedRootDomain = null
    return null
  }

  const parts = hostname.split('.')
  const PROBE = '__azzas_trk_probe'

  for (let i = parts.length - 2; i >= 0; i--) {
    const candidate = parts.slice(i).join('.')
    document.cookie = `${PROBE}=1; domain=.${candidate}; path=/;`

    if (document.cookie.includes(`${PROBE}=`)) {
      document.cookie = `${PROBE}=; domain=.${candidate}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
      cachedRootDomain = candidate
      return candidate
    }
  }

  cachedRootDomain = null
  return null
}

// Até quando cada promoção vale: 10 min se ninguém interagiu, 24h se tem produto anotado nela
function promoDeadline(promo: StoredPromotion, annotatedIds: Set<string>): number {
  return promo.saved_at + (annotatedIds.has(promo.id) ? ANNOTATED_TTL_MS : PROMO_TTL_MS)
}

function writePromoCookie(promoCookie: PromoCookieData) {
  if (!promoCookie.promos.length) return clearPromotion()

  // O cookie do browser expira quando a última promoção viva expirar
  const annotatedIds = new Set(Object.values(promoCookie.related_keys))
  const latestDeadline = Math.max(...promoCookie.promos.map((p) => promoDeadline(p, annotatedIds)))
  const remainingDays = (latestDeadline - Date.now()) / (24 * 60 * 60 * 1000)
  if (remainingDays <= 0) return

  setCookie(PROMO_COOKIE, JSON.stringify(promoCookie), remainingDays, resolveRootDomain())
}

export function savePromotion(input: PromotionInput) {
  const promoCookie = readPromoCookie() ?? { promos: [], related_keys: {} }
  const id = promotionId(input)
  const existing = promoCookie.promos.find((p) => p.id === id)

  if (existing) {
    existing.saved_at = Date.now() // clicou de novo no MESMO banner: só renova os 10 min dele, nada é apagado
  } else {
    promoCookie.promos.push({ ...input, id, saved_at: Date.now() })
  }

  // Passou de 5 promoções: apaga a mais antiga e as anotações dos produtos que vieram dela
  while (promoCookie.promos.length > MAX_PROMOTIONS) {
    const oldest = promoCookie.promos.reduce((a, b) => (a.saved_at <= b.saved_at ? a : b))
    promoCookie.promos = promoCookie.promos.filter((p) => p !== oldest)
    for (const [itemId, promoId] of Object.entries(promoCookie.related_keys)) {
      if (promoId === oldest.id) delete promoCookie.related_keys[itemId]
    }
  }

  writePromoCookie(promoCookie)
}

export function readPromoCookie(): PromoCookieData | null {
  const raw = getCookie(PROMO_COOKIE)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as PromoCookieData
    if (!Array.isArray(parsed.promos)) return null // cookie no formato antigo (versão anterior da lib): ignora, ele expira sozinho em 10 min

    // Mantém a promoção se ela ainda está no prazo dela: 10 min sem interação, 24h com produto anotado
    const now = Date.now()
    const annotatedIds = new Set(Object.values(parsed.related_keys ?? {}))
    const promos = parsed.promos.filter((p) => p.saved_at && now <= promoDeadline(p, annotatedIds))

    if (!promos.length) {
      clearPromotion()
      return null
    }

    // Se a promoção de um produto anotado já expirou, a anotação dele não serve mais: descarta
    const validIds = new Set(promos.map((p) => p.id))
    const related_keys: Record<string, string> = {}
    for (const [itemId, promoId] of Object.entries(parsed.related_keys ?? {})) {
      if (validIds.has(promoId)) related_keys[itemId] = promoId
    }

    return { promos, related_keys }
  } catch {
    return null
  }
}

export function clearPromotion() {
  setCookie(PROMO_COOKIE, '', -1, resolveRootDomain())
}

export function itemsWithPromotion(items: ItemsResponse[], eventName: EventName): ItemsResponse[] {
  const promoCookie = readPromoCookie()
  if (!promoCookie) return items

  // Só promoções ainda na janela de 10 min podem casar por vitrine (as mais velhas continuam vivas
  // só pelas anotações). Ordenadas do banner clicado mais recentemente pro mais antigo: se uma
  // vitrine casar com duas promoções ao mesmo tempo, vale a do último clique.
  const now = Date.now()
  const matchablePromos = promoCookie.promos
    .filter((p) => now - p.saved_at <= PROMO_TTL_MS)
    .sort((a, b) => b.saved_at - a.saved_at)
  const promosById = new Map(promoCookie.promos.map((p) => [p.id, p]))
  const canWrite = RELATED_KEYS_WRITERS.includes(eventName)

  let dirty = false
  const enrichedItems = items.map((item) => {
    // savedPromo = a promoção anotada pra esse produto no cookie (se tiver)
    // listMatch  = a promoção da vitrine onde o produto está aparecendo AGORA (se o item_list_name casar com a URL de algum banner)
    const savedPromo = item.item_id ? promosById.get(promoCookie.related_keys[item.item_id] ?? '') : undefined
    const listMatch = matchablePromos.find((p) => matchesPromotion(item.item_list_name, p))

    // A vitrine de agora manda. Se o evento não tem vitrine (checkout, PDP...), vale a anotação do cookie.
    const promotion = listMatch ?? savedPromo
    if (!promotion) return item

    // Evento com permissão de anotar + o produto casou com outra promoção: atualiza a anotação dele
    if (canWrite && item.item_id && promoCookie.related_keys[item.item_id] !== promotion.id) {
      promoCookie.related_keys[item.item_id] = promotion.id
      dirty = true
    }

    return {
      ...item,
      promotion_name: promotion.promotion_name,
      creative_name: promotion.creative_name,
      creative_slot: promotion.creative_slot,
    }
  })

  if (dirty) {
    // Passou de 30 produtos anotados: joga fora as anotações mais antigas e mantém as 30 mais recentes
    const entries = Object.entries(promoCookie.related_keys)
    if (entries.length > MAX_RELATED_KEYS) {
      promoCookie.related_keys = Object.fromEntries(entries.slice(entries.length - MAX_RELATED_KEYS))
    }
    writePromoCookie(promoCookie)
  }

  return enrichedItems
}
